/**
 * Canonical system energy state for operator-facing UI.
 * Display convention: battery power + = charging, − = discharging (see ENERGY_SEMANTICS).
 * Raw telemetry (backend): battPower + = discharging, − = charging; normalize at boundary.
 * Single source of truth: use getBatteryStateForDisplay (battery-led) or getSystemEnergyState (PV vs load).
 * Rule: No component computes charging/discharging/idle inline.
 */

/** ±100 W: band in which we treat as idle to prevent flicker at zero. */
export const IDLE_THRESHOLD_KW = 0.1;

/**
 * Normalize raw battery power to display convention.
 * Backend/adapters: + = discharging, − = charging (Watts).
 * Display: + = charging, − = discharging (kW).
 */
export function normalizeBatteryPowerForDisplay(rawBattPowerW: number): number {
  const w = Number(rawBattPowerW ?? 0);
  return w === 0 ? 0 : -w / 1000;
}

/**
 * Normalize raw grid power to display convention.
 * Backend/adapters: + = import, − = export (Watts). See supabase/functions/_shared/adapters/types.ts.
 * Display: + = export, − = import (kW). See ENERGY_FLOW_VISUALIZATION, fleet-types.
 */
export function normalizeGridPowerForDisplay(rawGridPowerW: number): number {
  const w = Number(rawGridPowerW ?? 0);
  return w === 0 ? 0 : -w / 1000;
}

/** Input for getGridKwForDisplay: grid power (W) and optional direction booleans from provider (e.g. Sol-Ark gridTo/toGrid). */
export interface GridDisplayInput {
  gridPower_w?: number;
  gridTo?: boolean;
  toGrid?: boolean;
}

/**
 * Grid power in display convention (kW): positive = export, negative = import.
 * When provider supplies direction booleans (e.g. Sol-Ark gridTo/toGrid), use them so display
 * matches actual flow even if the numeric sign from the API is wrong. Otherwise use
 * normalizeGridPowerForDisplay (canonical: + import, − export in Watts).
 */
export function getGridKwForDisplay(input: GridDisplayInput | null | undefined): number | null {
  if (input?.gridPower_w == null) return null;
  const magKw = Math.abs(input.gridPower_w) / 1000;
  if (magKw === 0) return null;
  if (input.gridTo === true) return -magKw; // import
  if (input.toGrid === true) return magKw;  // export
  return normalizeGridPowerForDisplay(input.gridPower_w);
}

/**
 * On-site load (home consumption only) for Energy Flow display.
 * Some inverters report "load" as house + grid export; when exporting, subtract export so Home
 * shows consumption only. gridKwDisplay in display convention: positive = export.
 */
export function onSiteLoadKwForDisplay(loadKw: number, gridKwDisplay: number | null): number {
  if (gridKwDisplay == null || gridKwDisplay <= 0) return loadKw;
  return Math.max(0, loadKw - gridKwDisplay);
}

/**
 * Providers whose "load" meter includes grid export when exporting.
 * Only these get load − export for Home in Energy Flow. When adding a new inverter: if its
 * load reading is house-only, do not add it; if it reports house + export (like Sol-Ark), add it here.
 */
const LOAD_INCLUDES_EXPORT_PROVIDERS: readonly string[] = ['solark'];

/**
 * Load (kW) to show at Home in Energy Flow, provider-aware.
 * Use this so we only subtract export for inverters that report load = house + export (e.g. Sol-Ark).
 * Others (e.g. house-only load) pass through unchanged so we don't double-subtract.
 */
export function getLoadKwForEnergyFlow(
  loadKw: number,
  gridKwDisplay: number | null,
  provider: string | null
): number {
  if (provider == null || !LOAD_INCLUDES_EXPORT_PROVIDERS.includes(provider)) return loadKw;
  return onSiteLoadKwForDisplay(loadKw, gridKwDisplay);
}

/**
 * Derive battery state for display: battery-led when display value is reliable, else PV vs load fallback.
 * Physics tiebreaker: when pvKw/loadKw are available and contradict the numeric sign (e.g. inverter
 * reports + when charging), prefer PV vs load so we never show "Discharging" when solar clearly exceeds load.
 */
export function getBatteryStateForDisplay(
  displayBattKw: number,
  pvKw?: number,
  loadKw?: number
): SystemEnergyState {
  if (pvKw != null && loadKw != null) {
    const surplus = pvKw - loadKw;
    if (surplus > IDLE_THRESHOLD_KW) return 'charging';
    if (-surplus > IDLE_THRESHOLD_KW) return 'discharging';
  }
  if (displayBattKw > IDLE_THRESHOLD_KW) return 'charging';
  if (displayBattKw < -IDLE_THRESHOLD_KW) return 'discharging';
  if (pvKw != null && loadKw != null) return getSystemEnergyState(pvKw, loadKw);
  return 'idle';
}

export type SystemEnergyState = 'charging' | 'discharging' | 'idle';

/**
 * Derives system charging/discharging state from PV vs load.
 * Charging: pv_kw - load_kw > IDLE_THRESHOLD_KW.
 * Discharging: load_kw - pv_kw > IDLE_THRESHOLD_KW.
 * Idle: otherwise.
 */
export function getSystemEnergyState(
  pvKw: number,
  loadKw: number
): SystemEnergyState {
  if (pvKw - loadKw > IDLE_THRESHOLD_KW) return 'charging';
  if (loadKw - pvKw > IDLE_THRESHOLD_KW) return 'discharging';
  return 'idle';
}

/**
 * Same semantics as getSystemEnergyState, with inputs in watts.
 * Use this when you have raw power values (e.g. from telemetry).
 */
export function getSystemEnergyStateFromWatts(params: {
  batteryW?: number;
  pvW: number;
  loadW: number;
  gridW?: number;
}): SystemEnergyState {
  return getSystemEnergyState(params.pvW / 1000, params.loadW / 1000);
}

/** Display casing for UI (Charging / Discharging / Idle). */
export type SystemEnergyStateDisplay = 'Charging' | 'Discharging' | 'Idle';

export function getSystemEnergyStateDisplay(state: SystemEnergyState): SystemEnergyStateDisplay {
  switch (state) {
    case 'charging': return 'Charging';
    case 'discharging': return 'Discharging';
    default: return 'Idle';
  }
}

/**
 * Dev-only: warn if UI would show "Discharging" while pv > load (logic regression).
 */
export function warnIfDischargingWhilePvExceedsLoad(
  label: string,
  pvKw: number,
  loadKw: number,
  displayedState: SystemEnergyState
): void {
  if (typeof import.meta !== 'undefined' && !import.meta.env?.DEV) return;
  if (displayedState !== 'discharging') return;
  if (pvKw - loadKw > IDLE_THRESHOLD_KW) {
    console.warn(
      `[energy-state] ${label}: UI shows Discharging but pv_kw (${pvKw.toFixed(2)}) > load_kw (${loadKw.toFixed(2)}). Use getSystemEnergyState(pv, load).`
    );
  }
}
